import MicIcon from "@mui/icons-material/Mic";
import MicNoneIcon from "@mui/icons-material/MicNone";
import MicOffIcon from "@mui/icons-material/MicOff";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import VolumeUpIcon from "@mui/icons-material/VolumeUp";
import VolumeUpOutlinedIcon from "@mui/icons-material/VolumeUpOutlined";
import {
  Box,
  Button,
  CircularProgress,
  LinearProgress,
  ListItemButton,
  Typography,
} from "@mui/material";
import ZoomVideo, {
  SubsessionAllocationPattern,
  TestMicrophoneReturn,
  TestSpeakerReturn,
} from "@zoom/videosdk";
import classNames from "classnames";
import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { getRealm } from "../AuthProvider";
import { ZoomMediaContext } from "../ZoomMediaProvider";
import Flex from "../components/Flex";
import LoadingOverlay from "../components/LoadingOverlay";
import Text from "../components/Text";
import { devConfig } from "../dev";
import { useSnackbar } from "../hooks/useSnackbar";
import { openJoinRoom } from "../redux/feature/join-room/JoinRoomSlice";
import { store } from "../redux/store";
import { VideoControllerApi } from "../services";
import { b64DecodeUnicode } from "../util";
import { getConfigObject, getErrorSnackbar } from "../utils/utils";
import ButtonWithOptions from "./NewComponents/ButtonWithOptions";
import PreviewCameraButton from "./NewComponents/PreviewCameraButton";
import PreviewMicrophoneButton from "./NewComponents/PreviewMicroPhoneButton";
import { useDuplicateRoomTab, useMount, useUnmount } from "./hooks";
import "./preview.scss";
import { useInviteJoinSubsession } from "./subsession/hooks/useInviteJoinRoom";
import { useSubsession } from "./subsession/hooks/useSubsession";
import { MediaDevice } from "./video-types";
import {
  CAMERA_START_FAILED_MESSAGE,
  MICROPHONE_START_FAILED_MESSAGE,
  ROOM_ALREADY_OPEN_MESSAGE,
  ROOM_ALREADY_OPEN_TITLE,
  ZOOM_CLIENT_CONNECTING_MESSAGE,
  ZOOM_CLIENT_INIT_TIMEOUT_MS,
  ZOOM_CLIENT_UNAVAILABLE_MESSAGE,
} from "./zoom-constant";
import { ZoomContext } from "./zoom-context";
import { PreviewMediaPreferences } from "./zoom-types";
import { applyPreviewMediaToSession } from "./zoom-utils";

let prevMicFeedbackStyle = "";
let micFeedBackInteval: any = "";

let localAudio = ZoomVideo.createLocalAudioTrack();
let localVideo = ZoomVideo.createLocalVideoTrack();
let allDevices;

const stopTrackQuietly = async (track?: {
  stop: () => Promise<void | Error>;
}) => {
  try {
    await track?.stop();
  } catch (error) {
    console.error("Error stopping track:", error);
  }
  console.error("Stopped track quietly");
};

/**
 * A local track cannot be resumed once it has been stopped — and it is stopped
 * whenever the devices are handed to the session or a device is switched.
 * Always capture on a freshly created track so a second attempt on the preview
 * works instead of silently rejecting.
 *
 * `enumerateDevices` reports empty device ids until the browser has granted
 * permission, so a remembered id can be blank or stale. Capturing on such an id
 * fails outright — fall back to the browser default rather than leaving the
 * preview dark.
 */
const startPreviewAudio = async (microphoneId?: string) => {
  await stopTrackQuietly(localAudio);
  localAudio = ZoomVideo.createLocalAudioTrack(microphoneId || undefined);
  try {
    await localAudio.start();
  } catch (error) {
    if (!microphoneId) throw error;
    localAudio = ZoomVideo.createLocalAudioTrack();
    await localAudio.start();
  }
};

const startPreviewVideo = async (
  element: HTMLVideoElement,
  cameraId?: string
) => {
  await stopTrackQuietly(localVideo);
  localVideo = ZoomVideo.createLocalVideoTrack(cameraId || undefined);
  try {
    await localVideo.start(element);
  } catch (error) {
    if (!cameraId) throw error;
    localVideo = ZoomVideo.createLocalVideoTrack();
    await localVideo.start(element);
  }
};

const mountDevices: () => Promise<{
  mics: MediaDevice[];
  speakers: MediaDevice[];
  cameras: MediaDevice[];
}> = async () => {
  allDevices = await ZoomVideo.getDevices();
  const cameraDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === "videoinput";
  });
  const micDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === "audioinput";
  });
  const speakerDevices: Array<MediaDeviceInfo> = allDevices.filter((device) => {
    return device.kind === "audiooutput";
  });
  return {
    mics: micDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    speakers: speakerDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
    cameras: cameraDevices.map((item) => {
      return { label: item.label, deviceId: item.deviceId };
    }),
  };
};

const updateMicFeedbackStyle = () => {
  const newVolumeIntensity = localAudio.getCurrentVolume();
  let newMicFeedbackStyle = "";

  if (newVolumeIntensity === 0) {
    newMicFeedbackStyle = "";
  } else if (newVolumeIntensity <= 0.05) {
    newMicFeedbackStyle = "mic-feedback__very-low";
  } else if (newVolumeIntensity <= 0.1) {
    newMicFeedbackStyle = "mic-feedback__low";
  } else if (newVolumeIntensity <= 0.15) {
    newMicFeedbackStyle = "mic-feedback__medium";
  } else if (newVolumeIntensity <= 0.2) {
    newMicFeedbackStyle = "mic-feedback__high";
  } else if (newVolumeIntensity <= 0.25) {
    newMicFeedbackStyle = "mic-feedback__very-high";
  } else {
    newMicFeedbackStyle = "mic-feedback__max";
  }
  const micIcon: any = document.getElementById("auido-volume-feedback");
  if (prevMicFeedbackStyle !== "" && micIcon) {
    micIcon.classList.toggle(prevMicFeedbackStyle);
  }

  if (newMicFeedbackStyle !== "" && micIcon) {
    micIcon.classList.toggle(newMicFeedbackStyle);
  }
  prevMicFeedbackStyle = newMicFeedbackStyle;
};

let meetingArgs: any = Object.fromEntries(new URLSearchParams(location.search));
// Add enforceGalleryView to turn on the gallery view without SharedAddayBuffer
if (!meetingArgs.topic || !meetingArgs.name || !meetingArgs.signature) {
  meetingArgs = {
    ...devConfig,
    ...meetingArgs,
  };
}

if (meetingArgs.web && meetingArgs.web !== "0") {
  ["topic", "name", "password", "sessionKey", "userIdentity"].forEach(
    (field) => {
      if (Object.hasOwn(meetingArgs, field)) {
        try {
          meetingArgs[field] = b64DecodeUnicode(meetingArgs[field]);
        } catch (e) {
          console.error(`Failed to decode ${field}:`, e);
        }
      }
    }
  );
  if (meetingArgs.role) {
    meetingArgs.role = parseInt(meetingArgs.role, 10);
  } else {
    meetingArgs.role = 1;
  }
}
// enforce use <video> tag render video, https://marketplacefront.zoom.us/sdk/custom/web/modules/Stream.html#attachVideo
meetingArgs.useVideoPlayer = 1;

[
  "enforceGalleryView",
  "enforceVB",
  "cloud_recording_option",
  "cloud_recording_election",
].forEach((field) => {
  if (Object.hasOwn(meetingArgs, field)) {
    try {
      meetingArgs[field] = Number(meetingArgs[field]);
    } catch (e) {
      meetingArgs[field] = 0;
    }
  }
});
if (meetingArgs?.telemetry_tracking_id) {
  try {
    meetingArgs.telemetry_tracking_id = b64DecodeUnicode(
      meetingArgs.telemetry_tracking_id
    );
  } catch (e) {
    console.error("Failed to decode telemetry tracking ID:", e);
  }
} else {
  meetingArgs.telemetry_tracking_id = "";
}

const PreviewZoomContent = ({ isPublicView }: { isPublicView?: boolean }) => {
  const [isStartedAudio, setIsStartedAudio] = useState<boolean>(false);
  const [isMuted, setIsMuted] = useState<boolean>(true);
  const [isStartedVideo, setIsStartedVideo] = useState<boolean>(false);
  const [micList, setMicList] = useState<MediaDevice[]>([]);
  const [speakerList, setSpeakerList] = useState<MediaDevice[]>([]);
  const [cameraList, setCameraList] = useState<MediaDevice[]>([]);
  const [inputLevel, setInputLevel] = useState(0);
  const [isPlayingAudio, setIsPlayingAudio] = useState(false);
  const [isInVBMode, setIsInVBMode] = useState(false);
  const speakerTesterRef = useRef<TestSpeakerReturn>();
  const microphoneTesterRef = useRef<TestMicrophoneReturn>();
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const zoomContext = useContext(ZoomContext);

  if (!zoomContext) {
    throw new Error("ZoomContext is not available. ");
  }

  const zmClient = zoomContext;
  const subsessionClient = zmClient.getSubsessionClient();
  const [isWaiting, setIsWaiting] = useState(false);
  const { createSubsessions } = useSubsession(zmClient, subsessionClient);
  const { invitedToJoin, inviteVisible, setInviteVisible, setInviteAccepted } =
    useInviteJoinSubsession(zmClient);
  const mediaContext = useContext(ZoomMediaContext);
  const showSnackbar = useSnackbar();
  if (!mediaContext) {
    throw new Error("ZoomMediaContext is not available.");
  }
  const {
    activeCamera,
    activeMicrophone,
    activeSpeaker,
    setActiveMicrophone,
    setActiveSpeaker,
    setActiveCamera,
  } = mediaContext;
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [searchParams] = useSearchParams();
  const role = searchParams.get("role") ?? "1";

  /* StrictMode runs mount effects twice; two concurrent captures on the same
     track leave it unusable, so only the first attempt is allowed through. */
  const isStartingPreviewAudio = useRef(false);

  const startAudioCapture = useCallback(
    async (microphoneId?: string) => {
      if (isStartingPreviewAudio.current) return;
      isStartingPreviewAudio.current = true;
      try {
        await startPreviewAudio(microphoneId);
        setIsStartedAudio(true);
        setIsMuted(true);
      } catch (error) {
        setIsStartedAudio(false);
        showSnackbar({
          type: "error",
          message: MICROPHONE_START_FAILED_MESSAGE,
        });
      } finally {
        isStartingPreviewAudio.current = false;
      }
    },
    [showSnackbar]
  );

  useEffect(() => {
    startAudioCapture();
  }, []);

  /**
   * The preview holds the camera and microphone through standalone local
   * tracks. They must be released before the session starts its own capture —
   * a camera still held by the local track is busy, and `startVideo` fails.
   */
  const releasePreviewDevices = async () => {
    if (micFeedBackInteval) {
      clearInterval(micFeedBackInteval);
      micFeedBackInteval = "";
    }
    speakerTesterRef.current?.destroy();
    speakerTesterRef.current = undefined;
    microphoneTesterRef.current?.destroy();
    microphoneTesterRef.current = undefined;

    await Promise.allSettled([
      isStartedAudio ? localAudio?.stop() : undefined,
      isStartedVideo ? localVideo?.stop() : undefined,
    ]);

    setIsStartedAudio(false);
    setIsStartedVideo(false);
    setIsPlayingAudio(false);
  };

  const join = async (token: string) => {
    const previewPreferences: PreviewMediaPreferences = {
      isVideoOn: isStartedVideo,
      isAudioMuted: isMuted,
      cameraId: activeCamera,
      microphoneId: activeMicrophone,
      speakerId: activeSpeaker,
    };

    await zmClient.join(
      searchParams.get("room") ?? "",
      token,
      role === "1"
        ? (searchParams.get("providerName") ?? "")
        : (searchParams.get("patientName") ?? "")
    );

    const stream = zmClient.getMediaStream();

    if (!stream) {
      return;
    }

    await releasePreviewDevices();

    const { isAudioStarted, isVideoStarted } = await applyPreviewMediaToSession(
      stream,
      previewPreferences
    );

    mediaContext.setMediaStream(stream);
    mediaContext.setIsStartedAudio(isAudioStarted);
    mediaContext.setIsAudioMuted(isAudioStarted ? isMuted : true);
    mediaContext.setIsStartedVideo(isVideoStarted);

    if (previewPreferences.isVideoOn && !isVideoStarted) {
      showSnackbar({ type: "error", message: CAMERA_START_FAILED_MESSAGE });
    }
    if (!isAudioStarted) {
      showSnackbar({ type: "error", message: MICROPHONE_START_FAILED_MESSAGE });
    }
  };

  const hostNameArr = location.hostname?.split(".");
  const xTENANTID = getRealm(hostNameArr);

  function getSignature(room: string) {
    return new VideoControllerApi(
      isPublicView ? undefined : getConfigObject()
    ).getAuthToken(room, undefined, xTENANTID);
  }

  useEffect(() => {
    if (
      inviteVisible &&
      isWaiting &&
      !zmClient?.getCurrentUserInfo()?.isHost &&
      invitedToJoin
    ) {
      subsessionClient?.joinSubsession(invitedToJoin.subsessionId);
      setInviteVisible(false);
      setInviteAccepted(true);
      setOpenJoinRoom();
    }
  }, [inviteVisible, zmClient, isWaiting]);

  const onCameraClick = useCallback(async () => {
    try {
      if (isStartedVideo) {
        await localVideo?.stop();
        setIsStartedVideo(false);
        setIsInVBMode(false);
        mediaContext.setIsStartedVideo(false);
        return;
      }

      if (!videoRef.current) return;

      await startPreviewVideo(videoRef.current, activeCamera);
      setIsStartedVideo(true);
      mediaContext.setIsStartedVideo(true);
    } catch (error) {
      setIsStartedVideo(false);
      showSnackbar({ type: "error", message: CAMERA_START_FAILED_MESSAGE });
    }
  }, [isStartedVideo, activeCamera]);

  const onMicrophoneClick = useCallback(async () => {
    if (!isStartedAudio) {
      await startAudioCapture(activeMicrophone);
      return;
    }

    try {
      if (isMuted) {
        await localAudio.unmute();
        micFeedBackInteval = setInterval(updateMicFeedbackStyle, 500);
        setIsMuted(false);
      } else {
        await localAudio.mute();
        if (micFeedBackInteval) {
          clearInterval(micFeedBackInteval);
          micFeedBackInteval = "";
        }
        setIsMuted(true);
      }
    } catch (error) {
      /* The track is gone — capture again so the button is not left dead. */
      setIsStartedAudio(false);
      await startAudioCapture(activeMicrophone);
    }
  }, [isStartedAudio, isMuted, activeMicrophone, startAudioCapture]);

  const onMicrophoneMenuClick = async (key: string) => {
    const [type, deviceId] = key.split("|");
    if (type === "microphone") {
      if (deviceId !== activeMicrophone) {
        await startAudioCapture(deviceId);
        setActiveMicrophone(deviceId);
      }
    } else if (type === "leave audio") {
      await localAudio.stop();
      setIsStartedAudio(false);
    }
  };

  useMount(() => {
    mountDevices().then((devices) => {
      setMicList(devices.mics);
      setCameraList(devices.cameras);
      setSpeakerList(devices.speakers);
      if (devices.speakers[0]?.deviceId) {
        setActiveSpeaker(devices.speakers[0].deviceId);
      }
      if (devices.mics[0]?.deviceId) {
        setActiveMicrophone(devices.mics[0].deviceId);
      }
      if (devices.cameras[0]?.deviceId) {
        setActiveCamera(devices.cameras[0].deviceId);
      }
    });
  });

  const onTestSpeakerClick = () => {
    if (microphoneTesterRef.current) {
      microphoneTesterRef.current.destroy();
      microphoneTesterRef.current = undefined;
    }
    if (isPlayingAudio) {
      speakerTesterRef.current?.stop();
      setIsPlayingAudio(false);
    } else {
      speakerTesterRef.current = localAudio.testSpeaker({
        speakerId: activeSpeaker,
      });
      setIsPlayingAudio(true);
    }
  };

  const onTestMicrophoneClick = () => {
    if (speakerTesterRef.current) {
      speakerTesterRef.current.destroy();
      speakerTesterRef.current = undefined;
    }
    microphoneTesterRef.current = localAudio.testMicrophone({
      microphoneId: activeMicrophone,
      speakerId: activeSpeaker,
      onAnalyseFrequency: (value) => {
        setInputLevel(Math.min(100, value));
      },
    });
  };

  useUnmount(() => {
    if (isStartedAudio) {
      localAudio.stop();
    }
    if (isStartedVideo) {
      localVideo.stop();
    }
  });

  return (
    <Flex>
      <LoadingOverlay isLoading={isLoading} />
      <Flex
        flexDirection={"column"}
        style={{
          marginTop: "170px",
          marginLeft: "50px",
        }}
      >
        <div className="container video-app">
          <div className="preview-video">
            <video
              className={classNames({ "preview-video-show": !isInVBMode })}
              muted={true}
              playsInline
              ref={videoRef}
            />
            <canvas
              className={classNames({ "preview-video-show": isInVBMode })}
              width="1280"
              height="720"
              ref={canvasRef}
            />
            <Flex className="custom-footer">
              <PreviewMicrophoneButton
                isStartedAudio={isStartedAudio}
                isMuted={isMuted}
                onMicrophoneClick={onMicrophoneClick}
                onMicrophoneMenuClick={onMicrophoneMenuClick}
                microphoneList={micList}
                speakerList={speakerList}
                activeMicrophone={activeMicrophone}
                activeSpeaker={activeSpeaker}
              />
              <PreviewCameraButton
                isStartedVideo={isStartedVideo}
                onCameraClick={onCameraClick}
              />
            </Flex>
          </div>
        </div>
        <Flex gap={1} marginTop={"10px"} mx={2}>
          <Box width={"100%"}>
            <ButtonWithOptions
              onOpenChange={(open) => {
                if (open && !isMuted) onTestMicrophoneClick();
              }}
              children={
                <Flex py={2} px={1}>
                  {isMuted ? <MicOffIcon /> : <MicIcon />}
                  <Box
                    sx={{
                      width: "100%",
                      flex: 1,
                      alignContent: "center",
                      marginRight: "10px",
                    }}
                  >
                    <LinearProgress variant="determinate" value={inputLevel} />
                  </Box>
                </Flex>
              }
              onChange={async (val) => {
                if (val && val !== activeMicrophone) {
                  if (mediaContext.mediaStream) {
                    await mediaContext.mediaStream.switchMicrophone(val);
                  } else {
                    await startAudioCapture(val);
                  }
                  setActiveMicrophone(val);
                }
              }}
              value={activeMicrophone}
              options={micList}
              label={
                micList?.find((sp) => sp.deviceId === activeMicrophone)
                  ?.label ?? ""
              }
              rightIcon={<MicNoneIcon />}
              // w={"250px"}
            />
          </Box>
          <Box width={"100%"}>
            <ButtonWithOptions
              onOpenChange={(open) => {
                if (!open) {
                  speakerTesterRef.current?.stop();
                  setIsPlayingAudio(false);
                }
              }}
              children={
                <ListItemButton
                  onClick={() => {
                    onTestSpeakerClick();
                  }}
                >
                  <Flex
                    justifyContent={"end"}
                    alignContent={"end"}
                    gap={1}
                    sx={{
                      marginRight: "10px",
                      marginTop: "10px",
                      marginBottom: "10px",
                    }}
                  >
                    <VolumeUpIcon />
                    <Text> Test Speakers</Text>
                  </Flex>
                </ListItemButton>
              }
              onChange={async (val) => {
                if (val && val !== activeSpeaker) {
                  if (mediaContext.mediaStream) {
                    await mediaContext.mediaStream.switchSpeaker(val);
                  }
                  setActiveSpeaker(val);
                }
              }}
              value={activeSpeaker}
              options={speakerList}
              label={
                speakerList?.find((sp) => sp.deviceId === activeSpeaker)
                  ?.label ?? ""
              }
              rightIcon={<VolumeUpOutlinedIcon />}
              // w={"250px"}
            />
          </Box>
          <Box width={"100%"}>
            <ButtonWithOptions
              onChange={async (val) => {
                if (val && val !== activeCamera) {
                  try {
                    if (mediaContext.mediaStream) {
                      await mediaContext.mediaStream.switchCamera(val);
                    } else if (isStartedVideo && videoRef.current) {
                      await startPreviewVideo(videoRef.current, val);
                    }
                    setActiveCamera(val);
                  } catch (error) {
                    showSnackbar({
                      type: "error",
                      message: CAMERA_START_FAILED_MESSAGE,
                    });
                  }
                }
              }}
              value={activeCamera}
              options={cameraList}
              label={
                cameraList?.find((sp) => sp.deviceId === activeCamera)?.label ??
                ""
              }
              rightIcon={<VideocamOutlinedIcon />}
            />
          </Box>
        </Flex>
      </Flex>
      <Flex marginLeft={"250px"} gap={2} alignItems={"center"}>
        <Flex
          flexDirection={"column"}
          gap={2}
          alignContent={"center"}
          alignItems={"center"}
        >
          {isWaiting ? (
            <>
              <Typography fontSize={"2rem"}>Asking to be let in...</Typography>
              <Text>You'll join the call when someone lets you in</Text>
              <CircularProgress />
            </>
          ) : (
            <>
              <Typography fontSize={"2rem"}>Ready to join?</Typography>
              <Text>No one else is here</Text>
              <Button
                variant="contained"
                onClick={() => {
                  setIsLoading(true);
                  getSignature(searchParams.get("room") ?? "")
                    .then((res: any) =>
                      join(res.data?.data?.authToken)
                        .then(() => {
                          if (role === "1") {
                            createSubsessions(
                              1,
                              SubsessionAllocationPattern.Manually
                            )?.then(() => {
                              setOpenJoinRoom();
                            });
                          } else {
                            setIsWaiting(true);
                          }
                        })
                        .catch(() => {
                          showSnackbar({
                            type: "error",
                            message: "Something went wrong",
                          });
                        })
                        .finally(() => {
                          setIsLoading(false);
                        })
                    )
                    .catch((e) => {
                      showSnackbar(getErrorSnackbar(e));
                    })
                    .finally(() => {
                      setIsLoading(false);
                    });
                }}
              >
                Start Appointment
              </Button>
              {role === "1" && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    const urlParams = new URLSearchParams(
                      window.location.search
                    );
                    const room = urlParams.get("room");
                    const providerName = urlParams.get("providerName");
                    const patientName = urlParams.get("patientName");
                    const startTime = urlParams.get("startTime");
                    const patientId = urlParams.get("patientid");
                    const appointmentId = urlParams.get("appointmentid");

                    const publicVideoUrl = `${window.location.origin}/public/video/preview?room=${room}&role=0&providerName=${providerName}&patientName=${patientName}&startTime=${startTime}&patientId=${patientId}&appointmentId=${appointmentId}`;

                    navigator.clipboard.writeText(publicVideoUrl);
                    showSnackbar({
                      type: "success",
                      message: "Patient link copied to clipboard",
                    });
                  }}
                >
                  Copy Patient Link
                </Button>
              )}
            </>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
};

/**
 * `ZoomClientProvider` builds its client inside an async effect, so both Zoom
 * contexts are null on the first render and only fill in once `client.init()`
 * resolves. Rendering the preview against a null client throws, which the app
 * error boundary turns into a full page "Something Went Wrong".
 *
 * A provider opening the preview from an app that has been running for a while
 * never saw this, because init had long since finished. A patient deep-links
 * straight into the call, so the preview mounted in the same commit as the
 * provider and always lost the race.
 *
 * Waiting for the contexts here keeps that throw as a genuine invariant for the
 * content below, which cannot mount until both are present.
 */
const PreviewZoom = ({ isPublicView }: { isPublicView?: boolean }) => {
  const zoomContext = useContext(ZoomContext);
  const mediaContext = useContext(ZoomMediaContext);
  const isZoomClientReady = !!zoomContext && !!mediaContext;
  const [hasTimedOut, setHasTimedOut] = useState(false);
  const [searchParams] = useSearchParams();
  const isDuplicateTab = useDuplicateRoomTab(
    searchParams.get("room") ?? undefined
  );

  useEffect(() => {
    if (isZoomClientReady) return;

    /* Init can also fail outright — an unsupported browser, or the SDK's CDN
       blocked — and leave the contexts null forever. Surface that instead of
       spinning indefinitely. */
    const timer = setTimeout(
      () => setHasTimedOut(true),
      ZOOM_CLIENT_INIT_TIMEOUT_MS
    );

    return () => clearTimeout(timer);
  }, [isZoomClientReady]);

  /* A second tab must not capture the devices or join the session — the first
     tab is the live call and both would fight over the same participant. */
  if (isDuplicateTab) {
    return (
      <Flex
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        gap={2}
        height="100vh"
        px={2}
        textAlign="center"
      >
        <Typography fontSize={"1.5rem"}>{ROOM_ALREADY_OPEN_TITLE}</Typography>
        <Text>{ROOM_ALREADY_OPEN_MESSAGE}</Text>
      </Flex>
    );
  }

  if (isZoomClientReady) {
    return <PreviewZoomContent isPublicView={isPublicView} />;
  }

  return (
    <Flex
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      gap={2}
      height="100vh"
    >
      {hasTimedOut ? (
        <Text>{ZOOM_CLIENT_UNAVAILABLE_MESSAGE}</Text>
      ) : (
        <>
          <CircularProgress />
          <Text>{ZOOM_CLIENT_CONNECTING_MESSAGE}</Text>
        </>
      )}
    </Flex>
  );
};

export default PreviewZoom;

const setOpenJoinRoom = () => {
  store.dispatch(openJoinRoom());
};
